import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import type { IAvatarValue } from '../../../../app/components/AvatarPicker';
import {
  avatarUploadFile,
  profileAvatarFormData,
} from '../../../../app/components/AvatarPicker/avatarUpload';
import { useAppSelector } from '../../../../app/store/hooks';
import avatar1 from '../../../../assets/images/avatars/avatar-man.svg';
import avatar2 from '../../../../assets/images/avatars/avatar-woman.svg';
import { useGetActiveCategoriesQuery } from '../../../category/infrastructure/api/category.api';
import { timezoneOptionLabel } from '../../../../helpers/timezone.helper';
import { useUserProfile } from '../../../user/ui/hooks/useUserProfile';
import {
  toStudentProfileFormValues,
  toUpdateStudentMeRequest,
} from '../../application/mapStudentProfile';
import {
  computeStudentProfileSteps,
  type StudentProfileStepId,
} from '../../application/studentProfileSteps';
import type { StudentProfileFormValues } from '../../application/types';
import {
  useGetStudentMeQuery,
  useUpdateStudentMeMutation,
} from '../../infrastructure/api/student-profile.api';

export const STUDENT_PROFILE_STEP_SECTION_IDS: Record<
  StudentProfileStepId,
  string
> = {
  basic: 'student-section-basic',
  goals: 'student-section-goals',
  categories: 'student-section-categories',
  languages: 'student-section-languages',
};

export const studentAvatarList = [
  { id: 1, src: avatar1, alt: 'avatar-1' },
  { id: 2, src: avatar2, alt: 'avatar-2' },
];

const defaultAvatar = (): IAvatarValue => ({
  src: studentAvatarList[0].src,
  type: 'avatar',
  confirmed: true,
});

const optionalUrl = (message: string) =>
  Yup.string().test('url', message, (value) => {
    if (!value || value.trim() === '') return true;
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });

export function useStudentProfileForm() {
  const { t } = useTranslation();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { view, clockLabel } = useUserProfile();
  const { data: student, isLoading, isError } = useGetStudentMeQuery(undefined, {
    skip: !accessToken,
  });
  const [updateStudentMe, { isLoading: isSaving }] = useUpdateStudentMeMutation();
  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetActiveCategoriesQuery(undefined, { skip: !accessToken });

  const [avatar, setAvatar] = useState<IAvatarValue>(defaultAvatar);
  const [initialAvatar, setInitialAvatar] = useState<IAvatarValue>(defaultAvatar());
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [activeStepId, setActiveStepId] = useState<StudentProfileStepId>('basic');
  const initialSnapshot = useRef<StudentProfileFormValues | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        displayName: Yup.string()
          .trim()
          .min(2, t('form.required', { ns: 'validation' }))
          .max(120)
          .required(t('form.required', { ns: 'validation' })),
        bio: Yup.string(),
        goals: Yup.string(),
        introVideoUrl: optionalUrl(t('form.invalid_url', { ns: 'validation' })),
        timezone: Yup.string().max(64),
        nativeLanguages: Yup.array().of(Yup.string()),
        categoryIds: Yup.array().of(Yup.string()),
      }),
    [t],
  );

  const form = useFormik<StudentProfileFormValues>({
    enableReinitialize: true,
    initialValues: toStudentProfileFormValues(student),
    validationSchema,
    onSubmit: async (values) => {
      if (isSaving) return;
      if (avatar.type === 'photo' && !avatar.confirmed) return;

      const file = await avatarUploadFile(avatar);
      const request = toUpdateStudentMeRequest({
        ...values,
        avatarUrl: file ? '' : values.avatarUrl,
      });
      await updateStudentMe(
        file
          ? profileAvatarFormData(
              request.profile ?? {},
              request.categoryIds ?? [],
              file,
              avatar.type,
            )
          : request,
      ).unwrap();
      setInitialAvatar(avatar);
      initialSnapshot.current = values;
      setIsEditingPhoto(false);
    },
  });

  useEffect(() => {
    if (!student || initialSnapshot.current) return;
    initialSnapshot.current = toStudentProfileFormValues(student);
  }, [student]);

  const initializeAvatar = useCallback(() => {
    const imageUrl = student?.profile?.avatarUrl;
    if (imageUrl) {
      const isPreset = studentAvatarList.some((item) => item.src === imageUrl);
      const next: IAvatarValue = {
        src: imageUrl,
        type: isPreset ? 'avatar' : 'photo',
        confirmed: true,
      };
      setAvatar(next);
      setInitialAvatar(next);
      return;
    }
    const fallback = defaultAvatar();
    setAvatar(fallback);
    setInitialAvatar(fallback);
  }, [student?.profile?.avatarUrl]);

  useEffect(() => {
    initializeAvatar();
  }, [initializeAvatar]);

  const stepsState = useMemo(
    () =>
      computeStudentProfileSteps(form.values, {
        hasAvatar: Boolean(avatar.src),
      }),
    [form.values, avatar.src],
  );

  const isAvatarChanged = avatar.src !== initialAvatar.src;
  const canSubmit =
    form.isValid &&
    (form.dirty || isAvatarChanged) &&
    (avatar.type === 'avatar' || Boolean(avatar.confirmed));
  const canReset = form.dirty || isAvatarChanged;

  const scrollToStep = useCallback((id: StudentProfileStepId) => {
    setActiveStepId(id);
    document
      .getElementById(STUDENT_PROFILE_STEP_SECTION_IDS[id])
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const onContinue = useCallback(() => {
    scrollToStep(stepsState.firstIncompleteId ?? 'basic');
  }, [scrollToStep, stepsState.firstIncompleteId]);

  const onCancel = useCallback(() => {
    if (initialSnapshot.current) {
      form.resetForm({ values: initialSnapshot.current });
    } else {
      form.resetForm();
    }
    setAvatar(initialAvatar);
    setIsEditingPhoto(false);
  }, [form, initialAvatar]);

  const displayName =
    form.values.displayName ||
    student?.profile?.displayName ||
    view?.displayName ||
    '…';
  const timezoneLabel =
    timezoneOptionLabel(form.values.timezone || student?.profile?.timezone) ||
    clockLabel;

  return {
    form,
    student,
    isLoading,
    isError,
    isSaving,
    canSubmit,
    canReset,
    stepsState,
    activeStepId,
    setActiveStepId,
    scrollToStep,
    onContinue,
    onCancel,
    displayName,
    timezoneLabel,
    avatar,
    isEditingPhoto,
    startPhotoEdit: () => setIsEditingPhoto(true),
    confirmPhoto: () => setIsEditingPhoto(false),
    onAvatarChange: (next: IAvatarValue) => {
      setAvatar(next);
      form.setFieldValue('avatarUrl', next.src);
    },
    onRemoveAvatar: () => setAvatar(defaultAvatar()),
    pictureSrc: avatar.src || student?.profile?.avatarUrl || view?.avatarUrl,
    gender: view?.gender,
    categories,
    isCategoriesLoading,
  };
}
